import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Equipment.css";

interface Character {
  name: string;
  heroCard: string;
  avatarImage: string;
  equipment: {
    weapon: { name: string; image: string; description: string; };
    armor: { name: string; image: string; description: string; };
    trinket: { name: string; image: string; description: string; };
  };
}

const characters: Character[] = [
  {
    name: "Mara",
    heroCard: "seekers/Mara.webp",
    avatarImage: "mara/mara.webp",
    equipment: {
      weapon: {
        name: "Fangs of Balthier",
        image: "mara/fangsofbalthier.webp",
        description: "Twin blades infused with dark energy"
      },
      armor: {
        name: "Vestments of the Veyratha",
        image: "mara/vestments of the veyratha.webp",
        description: "Ancient robes that grant mystical protection"
      },
      trinket: {
        name: "Bloodvine Choker",
        image: "mara/bloodvine choker.webp",
        description: "Mystical necklace that enhances shadow magic"
      }
    }
  },
  {
    name: "Theron", 
    heroCard: "seekers/theron.webp",
    avatarImage: "theron/theron.webp",
    equipment: {
      weapon: {
        name: "Flamecaller's Staff",
        image: "theron/flamecaller's staff.webp",
        description: "Staff that amplifies fire spells"
      },
      armor: {
        name: "Emberlord's Regalia",
        image: "theron/emberlord's regalia.webp", 
        description: "Majestic armor of the flame masters"
      },
      trinket: {
        name: "Ashen Loop",
        image: "theron/ashen loop.webp",
        description: "Ring that channels fire magic"
      }
    }
  }
];

interface SelectedCard {
  name: string;
  image: string;
  description: string;
  type: 'hero' | 'weapon' | 'armor' | 'trinket';
}

const Equipment: React.FC = () => {
  const [activeCharacter, setActiveCharacter] = useState(0);
  const [selectedCard, setSelectedCard] = useState<SelectedCard | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const handleCardClick = (name: string, image: string, description: string, type: 'hero' | 'weapon' | 'armor' | 'trinket') => {
    setSelectedCard({ name, image, description, type });
  };

  const currentCharacter = characters[activeCharacter];

  return (
    <div id="equipment" className="equipment-subsection" ref={ref}>
      <div className="container">
        <motion.div
          className="equipment-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="equipment-header" variants={itemVariants}>
            <h3>Equip Your Seeker for Battle</h3>
            <p>
              Each Seeker begins the game with three pieces of equipment attached: one Weapon, one Armor, and one Trinket. These cards grants powerful abilities and passive effects that shape the way your Seeker plays, providing an additional lever for both granular fantasy expression and strategic gameplay depth.
            </p>
          </motion.div>

          {/* Equipment Showcase */}
          <motion.div className="equipment-showcase" variants={itemVariants}>
            {/* Character Sidebar */}
            <div className="character-sidebar">
              {characters.map((character, index) => (
                <motion.button
                  key={character.name}
                  className={`character-tab ${
                    index === activeCharacter ? "active" : ""
                  }`}
                  onClick={() => setActiveCharacter(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="tab-face">
                    <img src={character.avatarImage} alt={character.name} />
                  </div>
                  <div className="tab-content">
                    <span className="character-name">{character.name}</span>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Equipment Display */}
            <div className="equipment-display">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCharacter}
                  className="equipment-grid"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                >
                  {/* Hero Card */}
                  <motion.div 
                    className="equipment-card hero-card"
                    onClick={() => handleCardClick(
                      currentCharacter.name,
                      currentCharacter.heroCard,
                      `${currentCharacter.name}, ready for battle`,
                      'hero'
                    )}
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={currentCharacter.heroCard} 
                      alt={currentCharacter.name}
                      loading="lazy"
                    />
                  </motion.div>

                  {/* Weapon */}
                  <motion.div 
                    className="equipment-card"
                    onClick={() => handleCardClick(
                      currentCharacter.equipment.weapon.name,
                      currentCharacter.equipment.weapon.image,
                      currentCharacter.equipment.weapon.description,
                      'weapon'
                    )}
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={currentCharacter.equipment.weapon.image} 
                      alt={currentCharacter.equipment.weapon.name}
                      loading="lazy"
                    />
                  </motion.div>

                  {/* Armor */}
                  <motion.div 
                    className="equipment-card"
                    onClick={() => handleCardClick(
                      currentCharacter.equipment.armor.name,
                      currentCharacter.equipment.armor.image,
                      currentCharacter.equipment.armor.description,
                      'armor'
                    )}
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={currentCharacter.equipment.armor.image} 
                      alt={currentCharacter.equipment.armor.name}
                      loading="lazy"
                    />
                  </motion.div>

                  {/* Trinket */}
                  <motion.div 
                    className="equipment-card"
                    onClick={() => handleCardClick(
                      currentCharacter.equipment.trinket.name,
                      currentCharacter.equipment.trinket.image,
                      currentCharacter.equipment.trinket.description,
                      'trinket'
                    )}
                    whileHover={{ scale: 1.02, y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img 
                      src={currentCharacter.equipment.trinket.image} 
                      alt={currentCharacter.equipment.trinket.name}
                      loading="lazy"
                    />
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Card Modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            className="card-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              className="card-modal"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close-btn"
                onClick={() => setSelectedCard(null)}
              >
                ×
              </button>
              <img src={selectedCard.image} alt={selectedCard.name} />
              <div className="modal-info">
                <h3>{selectedCard.name}</h3>
                <span className="card-type">{selectedCard.type}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Equipment;
